import { FC } from 'react';
import { useIntl } from 'react-intl';
import { Helmet } from 'react-helmet-async';
import cn from 'classnames';
import { useBoolean, useMediaQuery } from 'usehooks-ts';
import { faBars } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Drawer } from '@ebsco-ui/ebsco-ui';

import { Image, LinkElement } from '@app/components';
import { useBranding } from '@app/hooks';
import { breakpoint, Route } from '@app/constants';

import { AccountInfo } from './AccountInfo';
import { CatalogLink } from './CatalogLink';
import { NavigationPanel } from './NavigationPanel';

import css from './Navigation.module.scss';

export const Navigation: FC = () => {
  const { logo } = useBranding();
  const { $t } = useIntl();
  const { value: isDrawerOpen, toggle: toggleDrawer } = useBoolean(false);
  const isMobile = useMediaQuery(
    `(max-width: ${breakpoint.SCREEN.TABLET_LG - 1}px)`
  );

  const burgerButtonId = 'burgerButton';

  return (
    <div className={css.wrapper} data-auto="navigation-wrapper">
      <div className={cn(css.container, 'container')}>
        <div className={css.catalogLinks}>
          <LinkElement
            url={logo.link ?? Route.search}
            data-auto="logo"
            className={css.logoLink}
          >
            <Helmet>
              <link rel="preload" as="image" href={logo.url} />
            </Helmet>
            <Image
              className="logo"
              fetchpriority="high"
              src={logo.url}
              alt={logo.altText}
            />
          </LinkElement>
          {!isMobile && <CatalogLink />}
        </div>
        {isMobile ? (
          <div>
            <Button
              id={burgerButtonId}
              className={css.burgerButton}
              styleType="flat-icon"
              aria-label={$t({
                id: 'navigation.openNavigationMenu',
                defaultMessage: 'Open the navigation menu',
              })}
              onClick={toggleDrawer}
            >
              <FontAwesomeIcon icon={faBars} />
            </Button>
            <Drawer
              drawerTag="section"
              className={css.drawer}
              position="start"
              titleElementId={burgerButtonId}
              isOpen={isDrawerOpen}
              onClose={toggleDrawer}
            >
              <NavigationPanel isMobile toggleDrawer={toggleDrawer} />
            </Drawer>
          </div>
        ) : (
          <NavigationPanel />
        )}
        <div className={css.accountInfo} data-auto="account-info">
          <AccountInfo />
        </div>
      </div>
    </div>
  );
};
